import {
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateEmail as firebaseUpdateEmail,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  verifyPasswordResetCode,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import { auth } from '../config/firebase.config'
import {
  unsubscribeFromUserTopic,
  unsubscribeFromStaffCheckInAlerts,
  unsubscribeFromStaffTerminalAlerts,
  getFCMToken,
} from '@hapana/shared/web/fcm.service'

import { getAnalyticsClient } from '@hapana/shared/analytics/src'
import { cache as gqtyCache, resolve } from '@hapana/shared/web/services/gqty'
import { queryClient } from '../index'

// Import Zustand stores for reset
import useAuthStore from '../stores/auth.store'
import useSiteStore from '../stores/site.store'
import usePermissionsStore from '../stores/permissions.store'
import useCorporateStore from '../stores/corporate.store'
import { CLIENT_FILTERS_STORAGE_KEY, useFiltersStore } from '../pages/clients/store/useFiltersStore'

const clearAllStorage = () => {
  // Clear localStorage auth-related items
  const localStorageItemsToClear = [
    'activeLoginAsSession',
    'activeImpersonationSession',
    'authMechanism',
    'token',
    'keepFiltersChecked',
    CLIENT_FILTERS_STORAGE_KEY,
    'corporateSearchParams',
    'meQueryCache_v2', // Cached me query response
    'support-assistant-sidebar-open', // Support assistant sidebar state
  ]

  localStorageItemsToClear.forEach((key) => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.warn(`Failed to remove localStorage item: ${key}`, error)
    }
  })

  // Clear sessionStorage items
  const sessionStorageItemsToClear = [
    'sessionId',
    'authMechanism',
    'posFormCache',
    'demo-authenticated',
    CLIENT_FILTERS_STORAGE_KEY,
  ]

  sessionStorageItemsToClear.forEach((key) => {
    try {
      sessionStorage.removeItem(key)
    } catch (error) {
      console.warn(`Failed to remove sessionStorage item: ${key}`, error)
    }
  })

  // Clear all cookies for the current domain
  try {
    const cookies = document.cookie.split(';')
    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim()
      if (cookieName) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`
      }
    })
  } catch (error) {
    console.warn('Failed to clear cookies:', error)
  }
}

export const signOut = async () => {
  try {
    const currentUser = auth.currentUser
    const userId = currentUser?.uid

    // Terminate impersonation session if active
    try {
      const storedSession = localStorage.getItem('activeImpersonationSession')
      if (storedSession) {
        const sessionData = JSON.parse(storedSession)
        const sessionId = sessionData?.sessionId
        if (sessionId) {
          console.log('🔄 Terminating impersonation session:', sessionId)
          await resolve(({ mutation }) => {
            return mutation.terminateImpersonationSession({ sessionId })
          })
          console.log('✅ Impersonation session terminated successfully')
        }
      }
    } catch (error) {
      // Log error but don't block sign-out process
      // The session will be cleared from localStorage regardless
      console.warn('⚠️ Failed to terminate impersonation session:', error)
    }

    if (userId && import.meta.env.VITE_FCM_VAPID_KEY) {
      try {
        const { getMessaging, getToken } = await import('firebase/messaging')
        const fcmToken = await getFCMToken(auth.app, import.meta.env.VITE_FCM_VAPID_KEY, {
          getMessaging,
          getToken,
        })
        if (fcmToken) {
          await unsubscribeFromUserTopic(fcmToken)

          // Also release the site-scoped staff topics. The FCM token is tied to the
          // browser/service worker, not the auth session, so on a shared device it
          // would otherwise stay subscribed to this site's check-in/terminal alerts
          // and surface them for the next staff member / location that logs in.
          const siteId = useSiteStore.getState().currentSite?.siteId
          if (siteId) {
            await unsubscribeFromStaffCheckInAlerts(fcmToken, siteId)
            await unsubscribeFromStaffTerminalAlerts(fcmToken, siteId)
          }
        }
      } catch (fcmError) {
        console.warn('⚠️ Failed to unsubscribe from FCM topic:', fcmError)
      }
    }

    // Note: We intentionally do NOT clear tenant config here.
    // Tenant config is corporate/subdomain-specific, not user-specific.
    // It should only be refreshed when the subdomain changes. Clearing it left a signed-out tab
    // with no tenant, so another tab's sign-in synced in as auth/tenant-id-mismatch (BUG-502).

    // Clear all localStorage, sessionStorage, and cookies
    clearAllStorage()

    // Reset all Zustand stores
    useAuthStore.getState().reset()
    useSiteStore.getState().reset()
    usePermissionsStore.getState().reset()
    useCorporateStore.getState().reset()
    useFiltersStore.getState().resetStore()

    // Clear GQty cache to prevent stale data from previous user session
    gqtyCache.clear()

    // Clear React Query cache to prevent stale data from previous user session
    queryClient.clear()

    try {
      const analyticsClient = getAnalyticsClient()
      analyticsClient.trackUserLogout({
        user_type: 'admin',
        role: 'core_admin',
        site_id: 'core',
        session_duration_minutes: 0,
        logout_method: 'manual',
        pages_visited: 0, // TODO: Track this properly
        actions_performed: 0, // TODO: Track this properly
      })
    } catch (error) {
      console.warn('Analytics tracking failed:', error)
    }

    await firebaseSignOut(auth)
  } catch (error) {
    console.error('Sign out failed:', error)
    throw error
  }
}

export const forgotPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email)
  } catch (error) {
    console.error('Password reset failed:', error)
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email')
    }
    throw error
  }
}

export const updateEmail = async (newEmail) => {
  try {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }

    await firebaseUpdateEmail(user, newEmail)

    await sendEmailVerification(user)

    return {
      success: true,
      message: 'Email updated. Please verify your new email address.',
    }
  } catch (error) {
    console.error('Email update failed:', error)
    throw error
  }
}

export const setupAdminAsAuth = async () => {
  console.log('Admin auth not applicable for core app')
  return Promise.resolve()
}

export const completePassword = async () => {
  throw new Error('Complete password not applicable for Firebase auth')
}

export const resendConfirmationCode = async () => {
  const user = auth.currentUser
  if (user) {
    await sendEmailVerification(user)
    return { success: true }
  }
  throw new Error('No authenticated user')
}

export const requestPasswordReset = async (email) => {
  return await forgotPassword(email)
}

export const confirmPasswordReset = async (oobCode, newPassword) => {
  await firebaseConfirmPasswordReset(auth, oobCode, newPassword)
}

// Validate a password-reset oobCode without consuming it — throws when the code is
// expired, already used, or invalid. Used to surface an error before the member
// bothers entering a new password.
export const verifyResetCode = async (oobCode) => {
  return await verifyPasswordResetCode(auth, oobCode)
}

export const confirmStaffPasswordReset = async (oobCode, newPassword) => {
  const email = await verifyPasswordResetCode(auth, oobCode)
  await firebaseConfirmPasswordReset(auth, oobCode, newPassword)
  return email
}

export const signInWithPassword = async (email, password) => {
  await signInWithEmailAndPassword(auth, email, password)
}

export const adminSignInAsUser = async () => {
  throw new Error('Admin sign in not implemented for core app')
}

export const loginAsCoreSigOut = async () => {
  return await signOut()
}
